import {
    inject,
    Injectable,
    NgZone,
} from '@angular/core';

import {
    ConfirmDialogService,
} from 'src/app/dialogs/confirm-dialog/confirm-dialog.component';

import {
    AppLoaderService,
} from './app-loader.service';

import {
    CoreStateService,
} from './core.state.service';

/** How long the startup can go without any progress before the user is offered to reset the application. */
export const STARTUP_WATCHDOG_TIMEOUT = 300000;

/**
 * Finds a startup that is stuck. It is a timer that the startup keeps pushing forward (`kick`) every time it makes
 * progress and that it pauses while it waits for the user (`pauseWhile`). If it runs out, the user is asked whether the
 * application should be reset (its local data deleted).
 *
 * The question is closed when the startup moves on (`kick`) or ends (`disarm`): it is only worth asking while nothing
 * happens. The service is reached from AngularJS, outside the Angular zone, so everything that touches the dialog goes
 * through `zone.run`, or the dialog stays blank until something else triggers change detection.
 *
 * `disarm` ends it for good: the startup is over, or it ended in a state that handles the failure on its own. After that
 * `kick` does nothing, so code shared with the running application (a new login, for instance) cannot start it again.
 */
@Injectable({
    providedIn: 'root',
})
export class StartupWatchdogService {
    private readonly confirmDialog = inject(ConfirmDialogService);
    private readonly appLoader = inject(AppLoaderService);
    private readonly coreState = inject(CoreStateService);
    private readonly zone = inject(NgZone);

    private timer?: ReturnType<typeof setTimeout>;
    private disarmed = false;
    private dialog?: { close(): void };

    /** Starts the watchdog at the beginning of the startup. */
    public start(): void {
        this.disarmed = false;
        this.kick();
    }

    /** The startup made progress: the question is withdrawn if it is open, and the time runs from zero again. */
    public kick(): void {
        this.pause();
        this.closeQuestion();

        if (!this.disarmed) {
            this.timer = setTimeout(() => this.zone.run(() => this.expired()), STARTUP_WATCHDOG_TIMEOUT);
        }
    }

    /** Stops the time until the next `kick`. */
    public pause(): void {
        clearTimeout(this.timer);
        this.timer = undefined;
    }

    /** Pauses the watchdog while `task` runs, and starts the time again when it is over, whether it resolved or failed. */
    public async pauseWhile<T>(task: () => Promise<T>): Promise<T> {
        this.pause();

        try {
            return await task();
        } finally {
            this.kick();
        }
    }

    /** Ends the watchdog, and closes its question if it is on the screen. */
    public disarm(): void {
        this.disarmed = true;
        this.pause();
        this.closeQuestion();
    }

    private closeQuestion(): void {
        const dialog = this.dialog;

        this.dialog = undefined;

        if (dialog) {
            this.zone.run(() => dialog.close());
        }
    }

    private async expired(): Promise<void> {
        this.timer = undefined;
        this.appLoader.hide();

        const dialog = this.confirmDialog.open({
            data: { messageLabel: 'ERROR_STATES.STARTUP_NOT_RESPONDING' },
        });

        this.dialog = dialog;

        const answer = await new Promise<boolean | void>((resolve) => dialog.afterClosed().subscribe(resolve));

        if (this.dialog === dialog) {
            this.dialog = undefined;
        }

        if (answer === true && !this.disarmed) {
            this.coreState.resetApplication();
        }
    }
}
